// Romans 1 example data — used by ChapterChart (example view) and Editor (pre-fill)

export interface KeyTerm {
  term: string
  definition: string
  verses: number[]
}

export interface Verse {
  number: number
  text: string
}

export interface Paragraph {
  id: string
  number: number
  title: string
  startVerse: number
  endVerse: number
  verses: Verse[]
  topic: string
  summary: string
  keyPoints: string[]
  interpretiveNotes: string
  isKeyThesis?: boolean
}

/**
 * Logical relationship type (simplified Fuller / Piper arcing taxonomy).
 * Describes HOW one section supports or advances another.
 */
export type ArcRelationship =
  | 'grounds'      // G  — because/for — the reason behind the claim
  | 'inference'    // ∴  — therefore — conclusion drawn
  | 'purpose'      // P  — in order that — intended aim
  | 'result'       // R  — with the result that — actual outcome
  | 'progression'  // +  — building toward a climax
  | 'explanation'  // =  — that is / restatement
  | 'contrast'     // ≠  — but / although
  | 'series'       // ·  — and / likewise (parallel)

export interface Arc {
  label: string
  startVerse: number
  endVerse: number
  accent?: 'primary' | 'secondary'
  relationship?: ArcRelationship   // optional: labels the logical move
}

export interface ChartData {
  book: string
  chapter: number
  classification: string
  unity: string
  problem: string
  keyTerms: KeyTerm[]
  paragraphs: Paragraph[]
  interpretiveNotes: string
  arcs?: Arc[]
}

export const ROMANS_1_DATA: ChartData = {
  book: 'Romans',
  chapter: 1,
  classification: 'Epistle (formal theological letter)',
  unity: "Paul introduces himself and the gospel to the Romans, establishing that righteousness comes through faith and that God's wrath is revealed against all unrighteousness.",
  problem: 'To explain the gospel and his apostolic authority to the Roman church before visiting them.',
  arcs: [
    { label: 'I · Apostolic Introduction · vv.1–17', startVerse: 1,  endVerse: 17, accent: 'primary',   relationship: 'grounds'   },
    { label: 'II · Universal Guilt · vv.18–32',      startVerse: 18, endVerse: 32, accent: 'secondary', relationship: 'progression' },
  ],
  keyTerms: [
    { term: 'Gospel', definition: 'Good news of Jesus Christ and salvation', verses: [1, 9, 15, 16] },
    { term: 'Righteousness', definition: 'Being right with God through faith, not works', verses: [16, 17] },
    { term: 'Faith', definition: 'Trust and belief in Christ for salvation', verses: [5, 8, 17] },
    { term: 'Wrath', definition: "God's righteous judgment against sin and unbelief", verses: [18] },
  ],
  paragraphs: [
    {
      id: 'para1', number: 1, title: 'Greeting and Thanksgiving', startVerse: 1, endVerse: 7,
      verses: [
        { number: 1, text: 'Paul, a servant of Christ Jesus, called to be an apostle and set apart for the gospel of God—' },
        { number: 2, text: 'the gospel he promised beforehand through his prophets in the Holy Scriptures' },
        { number: 3, text: 'regarding his Son, who as to his earthly life was a descendant of David,' },
        { number: 4, text: 'and who through the Spirit of holiness was appointed the Son of God in power by his resurrection from the dead: Jesus Christ our Lord.' },
        { number: 5, text: "Through him and for his name's sake, we received grace and apostleship to call the Gentiles to the obedience that comes from faith." },
        { number: 6, text: 'And you also are among those Gentiles who are called to belong to Jesus Christ.' },
        { number: 7, text: 'To all in Rome who are loved by God and called to be his holy people: Grace and peace to you from God our Father and from the Lord Jesus Christ.' },
      ],
      topic: 'Apostolic Introduction',
      summary: 'Paul introduces himself as an apostle and describes his calling to preach the gospel to the Gentiles. He greets the Roman church with grace and peace.',
      keyPoints: ['Paul identifies himself as a servant and apostle', 'Gospel was promised through the prophets', 'Jesus is the Son of God, descended from David', "Resurrection demonstrates Jesus's power", "Paul's mission is to call Gentiles to faith and obedience"],
      interpretiveNotes: 'Paul establishes his credentials and authority. The greeting format was typical of Hellenistic letters but Paul Christianizes it with "grace and peace."',
    },
    {
      id: 'para2', number: 2, title: 'Desire to Visit and Preach', startVerse: 8, endVerse: 15,
      verses: [
        { number: 8, text: 'First, I thank my God through Jesus Christ for all of you, because your faith is being reported all over the world.' },
        { number: 9, text: 'God, whom I serve with my whole heart in preaching the gospel of his Son, is my witness how constantly I remember you' },
        { number: 10, text: "in my prayers at all times; and I pray that now at last by God's will the way may be opened for me to come to you." },
        { number: 11, text: 'I long to see you so that I may impart to you some spiritual gift to make you strong—' },
        { number: 12, text: 'that is, that you and I may be mutually encouraged by each other\'s faith.' },
        { number: 13, text: "I do not want you to be unaware, brothers and sisters, that I have often planned to come to you (but have been prevented from doing so until now) in order that I might have a harvest among you, just as I have had among the other Gentiles." },
        { number: 14, text: 'I am obligated both to Greeks and non-Greeks, both to the wise and the foolish.' },
        { number: 15, text: 'That is why I am so eager to preach the gospel also to you who are in Rome.' },
      ],
      topic: "Paul's Desire to Visit Rome",
      summary: "Paul expresses his thanksgiving for the Romans' faith, his constant prayers for them, and his strong desire to visit and minister to them.",
      keyPoints: ['Their faith is renowned throughout the world', 'Paul prays for them constantly', 'He desires to impart spiritual gifts to them', 'Mutual encouragement of faith', 'Part of his obligation to all peoples'],
      interpretiveNotes: 'Paul balances his authority with humility—he wants to give but also receive encouragement. This establishes a collegial relationship before addressing theological issues.',
    },
    {
      id: 'para3', number: 3, title: 'Theme: The Gospel and Righteousness', startVerse: 16, endVerse: 17,
      verses: [
        { number: 16, text: 'For I am not ashamed of the gospel, because it is the power of God that brings salvation to everyone who believes: first to the Jew, then to the Gentile.' },
        { number: 17, text: 'For in the gospel the righteousness of God is revealed—a righteousness that is by faith from first to last, just as it is written: "The righteous will live by faith."' },
      ],
      isKeyThesis: true,
      topic: "The Gospel's Power and Righteousness",
      summary: "Paul states the central theme: the gospel is God's power for salvation, revealing that righteousness comes through faith, not works.",
      keyPoints: ['Gospel is powerful and effective for salvation', 'Available to all believers (Jew and Gentile)', 'Righteousness is revealed through the gospel', 'Righteousness is by faith alone', 'Habakkuk 2:4 supports the faith principle'],
      interpretiveNotes: "This is Paul's thesis statement. The \"righteousness of God\" emphasizes both God's attribute and the imputed righteousness received by faith.",
    },
    {
      id: 'para4', number: 4, title: "God's Wrath Against Ungodliness", startVerse: 18, endVerse: 23,
      verses: [
        { number: 18, text: 'The wrath of God is being revealed from heaven against all the godlessness and wickedness of people, who suppress the truth by their wickedness,' },
        { number: 19, text: 'since what may be known about God is plain to them, because God has made it plain to them.' },
        { number: 20, text: "For since the creation of the world God's invisible qualities—his eternal power and divine nature—have been clearly seen, being understood from what has been made. So people are without excuse." },
        { number: 21, text: 'For although they knew God, they neither glorified him as God nor gave thanks to him, but their thinking became futile and their foolish hearts were darkened.' },
        { number: 22, text: 'Although they claimed to be wise, they became fools' },
        { number: 23, text: 'and exchanged the glory of the immortal God for images made to look like mortal human beings and birds and animals and reptiles.' },
      ],
      topic: "Universal Guilt Under God's Wrath",
      summary: "God's wrath is revealed against all unrighteousness. All people have evidence of God through creation but suppress the truth.",
      keyPoints: ["God's wrath is actively being revealed", 'Against all godlessness and unrighteousness', 'Truth is suppressed by wickedness', "Creation reveals God's eternal power", 'People are without excuse', 'Human hearts become darkened', 'Idolatry replaces the worship of God'],
      interpretiveNotes: 'Paul establishes universal human guilt. Natural revelation is sufficient to condemn but not sufficient to save.',
    },
    {
      id: 'para5', number: 5, title: 'Sexual Immorality as Result of Rejecting God', startVerse: 24, endVerse: 27,
      verses: [
        { number: 24, text: 'Therefore God gave them over in the sinful desires of their hearts to sexual impurity for the degrading of their bodies with one another.' },
        { number: 25, text: 'They exchanged the truth about God for a lie, and worshiped and served created things rather than the Creator—who is forever praised. Amen.' },
        { number: 26, text: 'Because of this, God gave them over to shameful lusts. Even their women exchanged natural sexual relations for unnatural ones.' },
        { number: 27, text: 'In the same way the men also abandoned natural relations with women and were inflamed with lust for one another. Men committed shameful acts with other men, and received in themselves the due penalty for their error.' },
      ],
      topic: 'Consequences of Idolatry: Sexual Sin',
      summary: 'Sexual immorality is presented as a consequence of rejecting God. God "gives people over" to their sinful desires as judgment.',
      keyPoints: ['God "gives over" sinners to their desires', 'Sexual impurity is degrading', 'Exchange of truth for a lie', 'Worship of created things instead of Creator'],
      interpretiveNotes: 'The phrase "God gave them over" (Gk. "paradidōmi") appears three times (vv. 24, 26, 28), showing progressive judgment.',
    },
    {
      id: 'para6', number: 6, title: 'Comprehensive List of Vices', startVerse: 28, endVerse: 31,
      verses: [
        { number: 28, text: 'Furthermore, just as they did not think it worthwhile to retain the knowledge of God, so God gave them over to a depraved mind, so that they do what ought not to be done.' },
        { number: 29, text: 'They have become filled with every kind of wickedness, evil, greed and depravity. They are full of envy, murder, strife, deceit and malice. They are gossips,' },
        { number: 30, text: 'slanderers, God-haters, insolent, arrogant and boastful; they invent ways of doing evil; they disobey their parents;' },
        { number: 31, text: 'they have no understanding, no fidelity, no love, no mercy.' },
      ],
      topic: 'The Catalog of Vices',
      summary: 'Paul provides an extensive list of sins that flow from abandoning the knowledge of God.',
      keyPoints: ['God gives sinners over to depraved thinking', 'Results in doing what is wrong', 'Comprehensive list of vices', 'Relational sins (envy, murder, strife)', 'Absence of virtues (no love, no mercy)'],
      interpretiveNotes: 'This catalog is not unique to Paul—similar vice lists appear in Greek philosophical texts. Paul frames it theologically: these sins result from abandoning God.',
    },
    {
      id: 'para7', number: 7, title: 'Knowledge of Judgment Does Not Stop Sin', startVerse: 32, endVerse: 32,
      verses: [
        { number: 32, text: "Although they know God's righteous decree that those who do such things deserve death, they not only continue to do these very things but also approve of those who practice them." },
      ],
      topic: "Willful Rejection of God's Judgment",
      summary: 'People not only persist in sin despite knowing God\'s judgment, but actively encourage others — demonstrating deliberate rebellion.',
      keyPoints: ["Sinners know God's righteous judgment", 'They know these acts deserve death', 'Yet they continue anyway', 'They approve and encourage others', 'This demonstrates willful rebellion'],
      interpretiveNotes: "This verse provides the transition to Romans 2: even knowing God's judgment doesn't save the self-righteous moralist who judges others while doing the same things.",
    },
  ],
  interpretiveNotes: "Romans 1 establishes the foundation for the entire epistle: all humans are under God's wrath due to sin and need the gospel. The passage progresses from theological introduction to universal human guilt, preparing for Paul's presentation of justification by faith in chapters 3–8.",
}
